/**
 * ProjectCommandAutocomplete - Project switching autocomplete for @project command
 * 
 * Shows a searchable list of projects the user can switch to.
 * Similar to GoCommandAutocomplete but for project switching.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { projectsApi } from '../../services/api';
import type { ProjectInstance } from '../../types';
import type { ActiveProject } from '../../contexts/AIContext';
import { logger } from '../../utils/logger';
import { useAutocompleteKeyboard } from '../../hooks/useAutocompleteKeyboard';

interface ProjectCommandAutocompleteProps {
  query: string;
  onSelect: (projectId: string | null) => void;
  onClose: () => void;
  position?: { top: number; left: number };
  activeProject: ActiveProject | null;
}

export default function ProjectCommandAutocomplete({
  query,
  onSelect,
  onClose,
  position,
  activeProject,
}: ProjectCommandAutocompleteProps) {
  const [projects, setProjects] = useState<ProjectInstance[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  
  // Fetch projects on mount
  useEffect(() => {
    setLoading(true);
    setError(null);
    projectsApi.listMyProjects()
      .then((data) => {
        setProjects(data);
      })
      .catch((err) => {
        logger.error('[ProjectCommandAutocomplete] Failed to load projects:', err);
        setError('Failed to load projects');
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);
  
  // Filter projects by query
  const filteredProjects = projects.filter(p =>
    p.projectCode.toLowerCase().includes(query.toLowerCase()) ||
    (p.customerName || '').toLowerCase().includes(query.toLowerCase()) ||
    (p.projectTitle || '').toLowerCase().includes(query.toLowerCase())
  );
  
  // Build items list: "Close Project" option (if active) + filtered projects
  interface MenuItem {
    type: 'close' | 'project';
    project?: ProjectInstance;
  }
  
  const items: MenuItem[] = [];
  
  // Add "Close Project" option if there's an active project
  if (activeProject && (!query || 'close project'.includes(query.toLowerCase()) || 'global'.includes(query.toLowerCase()))) {
    items.push({ type: 'close' });
  }
  
  filteredProjects.forEach(project => {
    items.push({ type: 'project', project });
  });
  
  // Handle selection
  const handleSelect = useCallback((item: MenuItem) => {
    if (item.type === 'close') {
      onSelect(null);
    } else if (item.project) {
      onSelect(item.project.id);
    }
    onClose();
  }, [onSelect, onClose]);

  const handleKeySelect = useCallback((index: number) => {
    if (items[index]) {
      handleSelect(items[index]);
    }
  }, [items, handleSelect]);

  const { selectedIndex } = useAutocompleteKeyboard({
    itemCount: items.length,
    onSelect: handleKeySelect,
    onClose,
    resetDeps: [query],
  });
  
  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);
  
  return (
    <div
      ref={containerRef}
      className="command-autocomplete"
      // eslint-disable-next-line no-restricted-syntax -- Dynamic position based on prop
      style={position ? { top: position.top, left: position.left } : undefined}
    >
      {/* Header */}
      <div className="px-4 py-2 bg-secondary border-b border-border">
        <div className="text-xs font-semibold text-muted-foreground">
          📁 Switch Project
        </div>
        {activeProject && (
          <div className="text-xs text-muted-foreground mt-1">
            Current: <span className="text-primary font-medium">{activeProject.projectCode}</span>
          </div>
        )}
      </div>
      
      {/* Results */}
      <div className="max-h-[400px] overflow-y-auto">
        {loading ? (
          <div className="px-4 py-8 text-center">
            <div className="text-2xl mb-2 animate-pulse">📁</div>
            <div className="text-sm text-muted-foreground">
              Loading projects...
            </div>
          </div>
        ) : error ? (
          <div className="px-4 py-8 text-center">
            <div className="text-2xl mb-2">⚠️</div>
            <div className="text-sm text-destructive">
              {error}
            </div>
          </div>
        ) : items.length === 0 ? (
          <div className="px-4 py-8 text-center">
            <div className="text-2xl mb-2">🔍</div>
            <div className="text-sm text-muted-foreground">
              No projects found{query ? ` for "${query}"` : ''}
            </div>
          </div>
        ) : (
          <>
            {items.map((item, index) => {
              const isSelected = selectedIndex === index;
              
              if (item.type === 'close') {
                return (
                  <div
                    key="close-project"
                    data-index={index}
                    onClick={() => handleSelect(item)}
                    className={`px-4 py-2.5 cursor-pointer flex items-center gap-3 transition-colors border-b border-border ${
                      isSelected
                        ? 'bg-destructive/10 text-destructive'
                        : 'text-destructive hover:bg-destructive/5'
                    }`}
                  >
                    <span className="text-lg w-6 text-center">✕</span>
                    <div className="flex-1">
                      <span className="font-medium">Close Project</span>
                      <span className="ml-2 text-xs opacity-70">Return to project list</span>
                    </div>
                    {isSelected && (
                      <span className="text-xs text-muted-foreground">↵</span>
                    )}
                  </div>
                );
              }
              
              const project = item.project!;
              const isCurrentProject = activeProject?.projectId === project.id;
              
              return (
                <div
                  key={project.id}
                  data-index={index}
                  onClick={() => handleSelect(item)}
                  className={`px-4 py-2.5 cursor-pointer flex items-center gap-3 transition-colors ${
                    isSelected
                      ? 'bg-info-light text-info'
                      : isCurrentProject
                        ? 'bg-primary/5 text-foreground'
                        : 'text-foreground hover:bg-muted'
                  }`}
                >
                  <span className="text-lg w-6 text-center">
                    {isCurrentProject ? '✓' : '📁'}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium flex items-center gap-2">
                      <span className="truncate">{project.projectCode}</span>
                      {isCurrentProject && (
                        <span className="text-xs bg-primary/20 text-primary px-1.5 py-0.5 rounded">
                          Current
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground truncate">
                      {project.customerName || project.projectTitle || 'No description'}
                    </div>
                  </div>
                  {isSelected && (
                    <span className="text-xs text-muted-foreground">↵</span>
                  )}
                </div>
              );
            })}
          </>
        )}
      </div>
      
      {/* Footer */}
      <div className="px-4 py-2 bg-muted border-t border-border text-xs text-muted-foreground">
        <div className="flex items-center justify-between">
          <span>↑↓ Navigate • Enter Select • Esc Close</span>
          <span>{projects.length} project{projects.length !== 1 ? 's' : ''}</span>
        </div>
      </div>
    </div>
  );
}
